<template>
  <div class="relative min-h-screen w-full bg-gradient-to-br from-gray-100 to-blue-50">
    <div class="container mx-auto px-4 sm:px-8 py-8">

      <!-- Page Header -->
      <nav class="text-sm text-gray-500 mb-5 flex items-center">
        <router-link to="/dashboard" class="hover:text-blue-700 font-medium">Dashboard</router-link>
        <span class="mx-1.5 text-gray-300">/</span>
        <span class="text-gray-800 font-semibold">Employers</span>
      </nav>

      <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p class="text-xs font-bold uppercase tracking-widest text-blue-600 m-0 mb-1">Employment</p>
          <h1 class="text-3xl font-extrabold text-gray-900 m-0">Employers</h1>
          <p class="text-gray-500 mt-1 mb-0 text-sm">
            {{ canEditEmployers
              ? 'View and update employer names across employment records.'
              : 'View employer names and student employment distribution.' }}
          </p>
        </div>
      </div>

      <!-- Summary tiles -->
      <div
        class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6"
        :class="canEditEmployers ? 'xl:grid-cols-4' : 'xl:grid-cols-3'"
      >
        <div class="em-stat-card em-stat-card--total">
          <div class="em-stat-card__icon">
            <font-awesome-icon :icon="['fas', 'building']" />
          </div>
          <div class="em-stat-card__body">
            <p class="em-stat-card__value">{{ formatNumber(stats.total_employers) }}</p>
            <p class="em-stat-card__label">Total Employers</p>
          </div>
        </div>

        <div class="em-stat-card em-stat-card--students">
          <div class="em-stat-card__icon">
            <font-awesome-icon :icon="['fas', 'user-graduate']" />
          </div>
          <div class="em-stat-card__body">
            <p class="em-stat-card__value">{{ formatNumber(stats.total_students) }}</p>
            <p class="em-stat-card__label">Students with Employer</p>
          </div>
        </div>

        <button
          type="button"
          class="em-stat-card em-stat-card--top em-stat-card--clickable text-left"
          :disabled="!stats.top_employer_name"
          @click="openTopEmployerDrilldown"
        >
          <div class="em-stat-card__icon">
            <font-awesome-icon :icon="['fas', 'trophy']" />
          </div>
          <div class="em-stat-card__body">
            <p class="em-stat-card__value">{{ formatNumber(stats.top_employer_students) }}</p>
            <p class="em-stat-card__label">Most Students</p>
            <p class="em-stat-card__hint" :title="stats.top_employer_name || ''">
              {{ stats.top_employer_name || '—' }}
            </p>
          </div>
        </button>

        <div v-if="canEditEmployers" class="em-stat-card em-stat-card--coverage">
          <div class="em-stat-card__icon">
            <font-awesome-icon :icon="['fas', 'clipboard-check']" />
          </div>
          <div class="em-stat-card__body">
            <p class="em-stat-card__value">
              {{ formatNumber(stats.in_list_count) }}
              <span class="em-stat-card__split">/ {{ formatNumber(stats.not_in_list_count) }}</span>
            </p>
            <p class="em-stat-card__label">In List / Not in List</p>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div
        class="grid grid-cols-1 gap-5 mb-6"
        :class="canEditEmployers ? 'xl:grid-cols-5' : ''"
      >
        <div :class="canEditEmployers ? 'xl:col-span-3' : ''">
          <top-employers-chart
            :data="stats.top_employers"
            @employer-clicked="onTopEmployerChartClick"
          ></top-employers-chart>
        </div>
        <div v-if="canEditEmployers" class="xl:col-span-2">
          <employer-list-coverage-chart
            :data="stats.coverage"
            @segment-clicked="handleCoverageSegmentClick"
          ></employer-list-coverage-chart>
        </div>
      </div>

      <!-- Employers table -->
      <section class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div class="flex items-center justify-between flex-wrap gap-4 px-6 md:px-8 pt-6 pb-4">
          <h2 class="text-lg font-bold text-gray-900 m-0">Employers List</h2>
          <div class="flex flex-wrap items-center gap-3 w-full sm:w-auto sm:justify-end">
            <select
              v-if="canEditEmployers"
              v-model="filterInEmployerList"
              class="em-select"
              title="Filter by employer list"
            >
              <option value="">In employer list: All</option>
              <option value="yes">In employer list: Yes</option>
              <option value="no">In employer list: No</option>
            </select>
            <div class="w-full sm:w-72">
              <search ref="searchRef" @on-search="onSearch" class-list="px-4 h-9"></search>
            </div>
            <button
              type="button"
              class="em-export-btn"
              title="Export Excel"
              :disabled="!hasItems && !hasActiveListFilters"
              @click="exportExcel"
            >
              <font-awesome-icon :icon="['fas', 'file-excel']" class="em-export-btn__icon" />
              <span>Export Excel</span>
            </button>
          </div>
        </div>

        <div v-if="loading" class="px-6 md:px-8 pb-6">
          <table-shimmer
            :titleHeaders="tableHeaders"
            :rows="perPage"
          ></table-shimmer>
        </div>

        <div v-else-if="!hasItems" class="py-20 text-center">
          <p class="text-lg font-bold text-gray-800 mb-1">No employers found</p>
          <p class="text-gray-500 text-sm mb-0">
            {{ hasActiveListFilters
              ? 'Try adjusting your search or filter.'
              : 'No employer names are available yet.' }}
          </p>
        </div>

        <div v-else>
          <div class="overflow-x-auto">
            <table class="min-w-full text-left text-sm">
              <thead>
                <tr class="em-thead-row">
                  <th class="em-th w-12">#</th>
                  <th class="em-th">
                    <button
                      type="button"
                      class="em-sort-btn"
                      :class="{ 'em-sort-btn--active': sortBy === 'name' }"
                      title="Sort by employer name"
                      @click="toggleSort('name')"
                    >
                      <span>Employer</span>
                      <font-awesome-icon :icon="sortIconFor('name')" class="em-sort-btn__icon" />
                    </button>
                  </th>
                  <th class="em-th whitespace-nowrap">
                    <button
                      type="button"
                      class="em-sort-btn"
                      :class="{ 'em-sort-btn--active': sortBy === 'students_count' }"
                      title="Sort by students count"
                      @click="toggleSort('students_count')"
                    >
                      <span>Students</span>
                      <font-awesome-icon :icon="sortIconFor('students_count')" class="em-sort-btn__icon" />
                    </button>
                  </th>
                  <th v-if="canEditEmployers" class="em-th whitespace-nowrap">In employer list</th>
                  <th v-if="canEditEmployers" class="em-th">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in employersList"
                  :key="row.name"
                  class="em-row"
                >
                  <td class="em-td font-medium whitespace-nowrap">
                    {{ rowNumber(index) }}
                  </td>
                  <td class="em-td">
                    <span class="font-semibold text-gray-900">{{ row.name }}</span>
                  </td>
                  <td class="em-td text-gray-700 whitespace-nowrap">
                    <button
                      v-if="row.students_count > 0"
                      type="button"
                      class="em-count-link"
                      :title="'View students for ' + row.name"
                      @click="openStudentsDrilldown(row)"
                    >
                      {{ formatNumber(row.students_count) }}
                    </button>
                    <span v-else>{{ formatNumber(row.students_count) }}</span>
                  </td>
                  <td v-if="canEditEmployers" class="em-td">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span
                        class="em-badge"
                        :class="row.in_employers_catalog ? 'em-badge--yes' : 'em-badge--no'"
                      >
                        {{ row.in_employers_catalog ? 'Yes' : 'No' }}
                      </span>
                      <button
                        v-if="!row.in_employers_catalog"
                        type="button"
                        class="em-btn-add"
                        title="Add to employer list"
                        :disabled="addingName === row.name"
                        @click="addToCatalog(row)"
                      >
                        {{ addingName === row.name ? 'Adding…' : 'Add' }}
                      </button>
                    </div>
                  </td>
                  <td v-if="canEditEmployers" class="em-td">
                    <div class="flex items-center gap-2">
                      <button
                        type="button"
                        title="Rename / standardize"
                        class="em-action-btn"
                        @click="openEdit(row)"
                      >
                        <font-awesome-icon :icon="['fas', 'pen']" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="paginatorInfoWithItems"
            class="px-6 md:px-8 py-4 border-t border-gray-100"
          >
            <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
          </div>
        </div>
      </section>
    </div>

    <!-- Rename modal -->
    <transition name="fade" mode="out-in">
      <div
        v-if="showFormModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
        @click.self="closeForm"
      >
        <div class="bg-white rounded-2xl border border-gray-200 shadow-xl max-w-lg w-full max-h-screen overflow-y-auto p-6">
          <h3 class="text-lg font-bold text-gray-900 mb-1">Rename employer</h3>
          <p class="text-sm text-gray-500 mb-5">
            Updates matching rows in students and employment_informations, ensures the new name exists in the employers catalog, and removes the old catalog entry.
          </p>

          <div class="mb-4">
            <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Current name
            </label>
            <input
              type="text"
              class="em-input em-input--readonly"
              :value="editingName"
              readonly
            />
          </div>

          <div class="mb-4">
            <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Standardized name
            </label>
            <input
              v-model="form.newName"
              type="text"
              class="em-input"
              placeholder="e.g. Abu Dhabi National Oil Company (ADNOC)"
            />
          </div>

          <div class="mb-6">
            <div class="flex items-center justify-between gap-2 mb-1">
              <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide m-0">
                Rename note
              </label>
              <span class="text-xs text-gray-400">Optional</span>
            </div>
            <textarea
              v-model="form.note"
              class="em-textarea"
              rows="3"
              maxlength="2000"
              placeholder="Why is this name being changed? e.g. official legal name, duplicate cleanup, spelling correction"
            ></textarea>
            <p class="text-xs text-gray-400 mt-1 mb-0">
              Saved to rename history and audit logs for future reference.
            </p>
          </div>

          <div class="flex justify-end gap-3">
            <button type="button" class="em-btn-outline" @click="closeForm">
              Cancel
            </button>
            <button
              type="button"
              class="em-btn-primary"
              :disabled="saving"
              @click="saveForm"
            >
              {{ saving ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <employer-students-drilldown-modal
      :visible="studentsDrilldownVisible"
      :employer-name="studentsDrilldownEmployer"
      :can-edit="canEditEmployers"
      @close="closeStudentsDrilldown"
    ></employer-students-drilldown-modal>

    <employer-coverage-drilldown-modal
      :visible="coverageDrilldownVisible"
      :in-employers-catalog="coverageDrilldownInCatalog"
      :segment-label="coverageDrilldownLabel"
      @close="handleCoverageDrilldownClose"
      @changed="handleCoverageDrilldownChanged"
    ></employer-coverage-drilldown-modal>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import xlsx from 'xlsx/dist/xlsx.full.min.js';
import Pagination from '../../components/shared/pagination.vue';
import TableShimmer from '../../components/shared/TableShimmer.vue';
import Search from '../../components/shared/search.vue';
import EmployerStudentsDrilldownModal from './EmployerStudentsDrilldownModal.vue';
import EmployerCoverageDrilldownModal from './EmployerCoverageDrilldownModal.vue';
import TopEmployersChart from './charts/TopEmployersChart.vue';
import EmployerListCoverageChart from './charts/EmployerListCoverageChart.vue';

const LIST_QUERY = gql`
  query ManagedEmployersList(
    $first: Int!
    $page: Int!
    $search: String
    $in_employers_catalog: Boolean
    $order_by: String
    $order_dir: String
  ) {
    managedEmployersList(
      first: $first
      page: $page
      search: $search
      in_employers_catalog: $in_employers_catalog
      order_by: $order_by
      order_dir: $order_dir
    ) {
      error
      error_code
      message
      data {
        name
        students_count
        in_employers_catalog
      }
      paginatorInfo {
        currentPage
        lastPage
        perPage
        total
        hasMorePages
        firstItem
        lastItem
      }
    }
  }
`;

var EXPORT_COLUMNS = [
  { key: 'name', header: 'EMPLOYER' },
  { key: 'students_count', header: 'STUDENTS' },
  { key: 'in_employers_catalog', header: 'IN EMPLOYER LIST' },
];

function mapEmployerExportRow(obj) {
  return {
    name: obj && obj.name ? obj.name : '',
    students_count:
      obj && obj.students_count != null ? Number(obj.students_count) : 0,
    in_employers_catalog: obj && obj.in_employers_catalog ? 'Yes' : 'No',
  };
}

function runDetachedEmployersListExport(ctx) {
  var store = ctx.store;
  var apolloClient = ctx.apolloClient;
  var jobId = ctx.jobId;
  var fileName = ctx.fileName;
  var variables = ctx.variables || {};
  var sheetLabel = ctx.sheetLabel || 'Employers';

  var allRows = [];
  var page = 1;
  var limit = 200;

  function fetchNext() {
    apolloClient
      .query({
        query: LIST_QUERY,
        fetchPolicy: 'network-only',
        variables: {
          first: limit,
          page: page,
          search: variables.search != null ? variables.search : null,
          in_employers_catalog:
            variables.in_employers_catalog !== undefined
              ? variables.in_employers_catalog
              : null,
          order_by: variables.order_by || 'name',
          order_dir: variables.order_dir || 'asc',
        },
      })
      .then(function (result) {
        var payload =
          result && result.data && result.data.managedEmployersList
            ? result.data.managedEmployersList
            : null;

        if (!payload || payload.error) {
          throw new Error(
            payload && payload.message ? payload.message : 'Server error'
          );
        }

        var batch = payload.data || [];
        var paginator = payload.paginatorInfo || {};

        if (batch.length > 0) {
          for (var i = 0; i < batch.length; i++) {
            allRows.push(mapEmployerExportRow(batch[i]));
          }

          var total = paginator.total || allRows.length;
          var percent =
            total > 0
              ? Math.min(95, Math.round((allRows.length / total) * 100))
              : Math.min(
                  95,
                  Math.round(
                    (allRows.length / Math.max(allRows.length + limit, 1)) * 100
                  )
                );

          store.dispatch('exportManager/updateJob', {
            id: jobId,
            progress: percent,
            currentSheet: sheetLabel + ' (' + allRows.length + ' rows)',
          });
        }

        if (paginator.hasMorePages) {
          page = page + 1;
          fetchNext();
        } else {
          finishExport();
        }
      })
      .catch(function (error) {
        if (allRows.length > 0) {
          finishExport();
        } else {
          store.dispatch('exportManager/failJob', {
            id: jobId,
            error: error && error.message ? error.message : 'Export failed',
          });
        }
      });
  }

  function finishExport() {
    try {
      if (allRows.length === 0) {
        store.dispatch('exportManager/failJob', {
          id: jobId,
          error: 'No data available for export',
        });
        return;
      }

      var colKeys = EXPORT_COLUMNS.map(function (col) {
        return col.key;
      });

      var rows = allRows.map(function (row) {
        var out = {};
        for (var i = 0; i < EXPORT_COLUMNS.length; i++) {
          var key = EXPORT_COLUMNS[i].key;
          out[key] = row[key] !== undefined && row[key] !== null ? row[key] : '';
        }
        return out;
      });

      var worksheet = xlsx.utils.json_to_sheet(rows, { header: colKeys });

      var columnWidths = [];
      for (var c = 0; c < EXPORT_COLUMNS.length; c++) {
        var column = String.fromCharCode(65 + (c % 26));
        var r = Math.floor(c / 26);
        var columnLetter =
          r > 0 ? String.fromCharCode(64 + r) + column : column;
        worksheet[columnLetter + '1'] = {
          v: EXPORT_COLUMNS[c].header,
          t: 's',
        };
        columnWidths.push({
          width: Math.max(18, EXPORT_COLUMNS[c].header.length + 5),
        });
      }

      worksheet['!cols'] = columnWidths;

      var workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, 'Employers');
      xlsx.writeFile(workbook, fileName);

      store.dispatch('exportManager/completeJob', {
        id: jobId,
        rowCount: allRows.length,
      });
    } catch (error) {
      store.dispatch('exportManager/failJob', {
        id: jobId,
        error: error && error.message ? error.message : 'Export failed',
      });
    }
  }

  fetchNext();
}

const STATS_QUERY = gql`
  query ManagedEmployersStatistics {
    managedEmployersStatistics {
      error
      error_code
      message
      data {
        total_employers
        total_students
        in_list_count
        not_in_list_count
        top_employer_name
        top_employer_students
        top_employers {
          name
          count
        }
        coverage {
          label
          count
        }
      }
    }
  }
`;

const RENAME_MUTATION = gql`
  mutation RenameManagedEmployer($old_name: String!, $new_name: String!, $note: String) {
    renameManagedEmployer(old_name: $old_name, new_name: $new_name, note: $note) {
      error
      error_code
      message
      data {
        old_name
        new_name
        note
        students_updated
        employment_informations_updated
        employer_inserted
        employer_deleted
      }
    }
  }
`;

const ADD_TO_CATALOG_MUTATION = gql`
  mutation AddManagedEmployerToCatalog($name: String!) {
    addManagedEmployerToCatalog(name: $name) {
      error
      error_code
      message
      data {
        name
        employer_inserted
      }
    }
  }
`;

export default {
  name: 'employers-index',
  components: {
    Pagination,
    TableShimmer,
    Search,
    EmployerStudentsDrilldownModal,
    EmployerCoverageDrilldownModal,
    TopEmployersChart,
    EmployerListCoverageChart,
  },
  data() {
    return {
      employersList: [],
      paginatorInfo: null,
      loading: false,
      statsLoading: false,
      perPage: 100,
      page: 1,
      searchText: '',
      filterInEmployerList: '',
      sortBy: 'name',
      sortDir: 'asc',
      showFormModal: false,
      editingName: '',
      saving: false,
      addingName: '',
      studentsDrilldownVisible: false,
      studentsDrilldownEmployer: '',
      coverageDrilldownVisible: false,
      coverageDrilldownInCatalog: false,
      coverageDrilldownLabel: '',
      stats: {
        total_employers: 0,
        total_students: 0,
        in_list_count: 0,
        not_in_list_count: 0,
        top_employer_name: null,
        top_employer_students: 0,
        top_employers: [],
        coverage: [],
      },
      form: {
        newName: '',
        note: '',
      },
    };
  },
  computed: {
    canEditEmployers() {
      return this.hasPermission('employers-update');
    },
    tableHeaders() {
      if (this.canEditEmployers) {
        return ['#', 'Employer', 'Students', 'In employer list', 'Actions'];
      }
      return ['#', 'Employer', 'Students'];
    },
    hasItems() {
      return this.employersList && this.employersList.length > 0;
    },
    hasActiveListFilters() {
      var search = (this.searchText || '').trim();
      return !!search || !!this.filterInEmployerList;
    },
    inEmployersCatalogFilter() {
      if (this.filterInEmployerList === 'yes') {
        return true;
      }
      if (this.filterInEmployerList === 'no') {
        return false;
      }
      return null;
    },
    paginatorInfoWithItems() {
      if (!this.paginatorInfo) {
        return null;
      }
      var pi = this.paginatorInfo;
      var firstItem =
        pi.firstItem ||
        (pi.currentPage - 1) * pi.perPage + 1;
      var lastItem =
        pi.lastItem ||
        Math.min(pi.currentPage * pi.perPage, pi.total);
      return {
        currentPage: pi.currentPage,
        lastPage: pi.lastPage,
        perPage: pi.perPage,
        total: pi.total,
        hasMorePages: pi.hasMorePages,
        firstItem: firstItem,
        lastItem: lastItem,
      };
    },
  },
  watch: {
    searchText() {
      this.page = 1;
      this.loadList();
    },
    filterInEmployerList() {
      this.page = 1;
      this.loadList();
    },
  },
  created() {
    this.loadStatistics();
    this.loadList();
  },
  methods: {
    formatNumber(value) {
      var n = Number(value);
      if (isNaN(n)) {
        return '0';
      }
      return n.toLocaleString();
    },
    emptyStats() {
      return {
        total_employers: 0,
        total_students: 0,
        in_list_count: 0,
        not_in_list_count: 0,
        top_employer_name: null,
        top_employer_students: 0,
        top_employers: [],
        coverage: [],
      };
    },
    async loadStatistics() {
      this.statsLoading = true;
      try {
        var result = await this.$apollo.query({
          query: STATS_QUERY,
          fetchPolicy: 'network-only',
        });
        var data = result && result.data ? result.data : null;
        var payload = data && data.managedEmployersStatistics
          ? data.managedEmployersStatistics
          : null;
        if (!payload || payload.error) {
          this.stats = this.emptyStats();
        } else {
          var statsData = payload.data || {};
          this.stats = {
            total_employers: statsData.total_employers || 0,
            total_students: statsData.total_students || 0,
            in_list_count: statsData.in_list_count || 0,
            not_in_list_count: statsData.not_in_list_count || 0,
            top_employer_name: statsData.top_employer_name || null,
            top_employer_students: statsData.top_employer_students || 0,
            top_employers: statsData.top_employers || [],
            coverage: statsData.coverage || [],
          };
        }
      } catch (e) {
        this.stats = this.emptyStats();
      } finally {
        this.statsLoading = false;
      }
    },
    toastAlert(options) {
      if (!this.$swal) {
        return Promise.resolve({});
      }
      var merged = Object.assign(
        {
          timer: 2000,
        },
        options
      );
      if (
        (merged.icon === 'error' || merged.icon === 'warning') &&
        (options.timer === undefined || options.timer === null)
      ) {
        merged.timer = 3500;
      }
      var self = this;
      var Toast = this.$swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timerProgressBar: true,
        didOpen: function (toast) {
          toast.addEventListener('mouseenter', self.$swal.stopTimer);
          toast.addEventListener('mouseleave', self.$swal.resumeTimer);
        },
      });
      return Toast.fire(merged);
    },
    hasPermission(crudName) {
      // User-specific grants only — no role / superadmin bypass for this module.
      var permissions = this.$store.getters.permissions;
      if (!permissions) {
        return false;
      }
      var permissionsArray = permissions.split(',');
      if (permissionsArray.includes(crudName)) {
        return true;
      }
      return false;
    },
    rowNumber(index) {
      if (this.paginatorInfoWithItems && this.paginatorInfoWithItems.firstItem) {
        return this.paginatorInfoWithItems.firstItem + index;
      }
      return index + 1;
    },
    onSearch(searchText) {
      this.searchText = searchText;
    },
    exportExcel() {
      var store = this.$store;
      var apolloClient =
        this.$apollo && this.$apollo.provider
          ? this.$apollo.provider.defaultClient
          : null;
      if (!apolloClient) {
        this.toastAlert({
          icon: 'error',
          title: 'Export failed',
          text: 'Unable to start export.',
        });
        return;
      }

      var search = (this.searchText || '').trim();
      var fileName = 'Employers_List.xlsx';
      var sheetLabel = 'Employers';
      var variables = {
        search: search ? search : null,
        in_employers_catalog: this.canEditEmployers
          ? this.inEmployersCatalogFilter
          : null,
        order_by: this.sortBy || 'name',
        order_dir: this.sortDir || 'asc',
      };

      store
        .dispatch('exportManager/addJob', {
          fileName: fileName,
          totalSheets: 1,
        })
        .then(function (jobId) {
          store.dispatch('exportManager/setMinimized', false);
          store.dispatch('exportManager/updateJob', {
            id: jobId,
            progress: 5,
            currentSheet: sheetLabel,
          });

          runDetachedEmployersListExport({
            store: store,
            apolloClient: apolloClient,
            jobId: jobId,
            fileName: fileName,
            sheetLabel: sheetLabel,
            variables: variables,
          });
        });
    },
    sortIconFor(column) {
      if (this.sortBy !== column) {
        return ['fas', 'sort'];
      }
      if (this.sortDir === 'desc') {
        return ['fas', 'sort-down'];
      }
      return ['fas', 'sort-up'];
    },
    toggleSort(column) {
      if (column !== 'name' && column !== 'students_count') {
        return;
      }
      if (this.sortBy === column) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = column;
        // First click on students defaults to highest first; name defaults to A–Z.
        this.sortDir = column === 'students_count' ? 'desc' : 'asc';
      }
      this.page = 1;
      this.loadList();
    },
    async loadList() {
      this.loading = true;
      try {
        var search = (this.searchText || '').trim();
        var result = await this.$apollo.query({
          query: LIST_QUERY,
          variables: {
            first: this.perPage,
            page: this.page,
            search: search ? search : null,
            in_employers_catalog: this.canEditEmployers
              ? this.inEmployersCatalogFilter
              : null,
            order_by: this.sortBy || 'name',
            order_dir: this.sortDir || 'asc',
          },
          fetchPolicy: 'network-only',
        });
        var data = result && result.data ? result.data : null;
        var payload = data && data.managedEmployersList ? data.managedEmployersList : null;
        if (!payload) {
          throw new Error('No response');
        }
        if (payload.error) {
          this.toastAlert({
            icon: 'error',
            title: 'Unable to load',
            text: payload.message,
          });
          this.employersList = [];
          this.paginatorInfo = null;
        } else {
          this.employersList = payload.data || [];
          this.paginatorInfo = payload.paginatorInfo;
        }
      } catch (e) {
        this.toastAlert({
          icon: 'error',
          title: 'Failed',
          text: 'Could not load employers.',
        });
        this.employersList = [];
        this.paginatorInfo = null;
      } finally {
        this.loading = false;
      }
    },
    setPage(p) {
      this.page = p;
      this.loadList();
    },
    openEdit(row) {
      this.editingName = row.name;
      this.form = { newName: row.name, note: '' };
      this.showFormModal = true;
    },
    openStudentsDrilldown(row) {
      if (!row || !row.name || !row.students_count) {
        return;
      }
      this.studentsDrilldownEmployer = row.name;
      this.studentsDrilldownVisible = true;
    },
    openTopEmployerDrilldown() {
      if (!this.stats.top_employer_name) {
        return;
      }
      this.studentsDrilldownEmployer = this.stats.top_employer_name;
      this.studentsDrilldownVisible = true;
    },
    onTopEmployerChartClick(employerName) {
      if (!employerName) {
        return;
      }
      this.studentsDrilldownEmployer = employerName;
      this.studentsDrilldownVisible = true;
    },
    closeStudentsDrilldown() {
      this.studentsDrilldownVisible = false;
      this.studentsDrilldownEmployer = '';
    },
    handleCoverageSegmentClick(payload) {
      if (!payload) {
        return;
      }
      this.coverageDrilldownInCatalog = !!payload.in_employers_catalog;
      this.coverageDrilldownLabel = payload.label || '';
      this.coverageDrilldownVisible = true;
    },
    handleCoverageDrilldownClose() {
      this.coverageDrilldownVisible = false;
      this.coverageDrilldownLabel = '';
      this.coverageDrilldownInCatalog = false;
    },
    handleCoverageDrilldownChanged() {
      this.loadList();
      this.loadStatistics();
    },
    async addToCatalog(row) {
      var name = row && row.name ? String(row.name).trim() : '';
      if (!name) {
        return;
      }
      if (!this.hasPermission('employers-update')) {
        return;
      }

      this.addingName = name;
      try {
        var result = await this.$apollo.mutate({
          mutation: ADD_TO_CATALOG_MUTATION,
          variables: { name: name },
        });
        var payload =
          result && result.data && result.data.addManagedEmployerToCatalog
            ? result.data.addManagedEmployerToCatalog
            : null;

        if (!payload) {
          throw new Error('No response');
        }

        if (payload.error) {
          this.toastAlert({
            icon: 'error',
            title: 'Error',
            text: payload.message,
          });
        } else {
          this.toastAlert({
            icon: 'success',
            title: 'Added',
            text: payload.message || 'Employer added to the list.',
            timer: 2000,
          });
          this.loadList();
          this.loadStatistics();
        }
      } catch (err) {
        this.toastAlert({
          icon: 'error',
          title: 'Failed',
          text: 'Could not add employer to the list.',
        });
      } finally {
        this.addingName = '';
      }
    },
    closeForm() {
      this.showFormModal = false;
      this.editingName = '';
      this.form = { newName: '', note: '' };
    },
    async saveForm() {
      var oldName = (this.editingName || '').trim();
      var newName = (this.form.newName || '').trim();
      var note = (this.form.note || '').trim();

      if (!oldName || !newName) {
        this.toastAlert({
          icon: 'warning',
          title: 'Missing fields',
          text: 'Standardized employer name is required.',
        });
        return;
      }

      if (oldName === newName) {
        this.toastAlert({
          icon: 'warning',
          title: 'No change',
          text: 'Enter a different standardized name.',
        });
        return;
      }

      if (!this.$swal) {
        return;
      }

      var confirmHtml =
        '<div class="text-left text-sm">' +
        '<p class="mb-2">This will update all matching employment records.</p>' +
        '<p><strong>From:</strong> ' + this.escapeHtml(oldName) + '</p>' +
        '<p><strong>To:</strong> ' + this.escapeHtml(newName) + '</p>';
      if (note) {
        confirmHtml +=
          '<p class="mt-2"><strong>Note:</strong> ' + this.escapeHtml(note) + '</p>';
      }
      confirmHtml += '</div>';

      var confirm = await this.$swal.fire({
        title: 'Rename employer?',
        html: confirmHtml,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#2563eb',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, rename',
      });

      if (!confirm || !confirm.isConfirmed) {
        return;
      }

      this.saving = true;
      try {
        var result = await this.$apollo.mutate({
          mutation: RENAME_MUTATION,
          variables: {
            old_name: oldName,
            new_name: newName,
            note: note ? note : null,
          },
        });
        var payload =
          result && result.data && result.data.renameManagedEmployer
            ? result.data.renameManagedEmployer
            : null;

        if (!payload) {
          throw new Error('No response');
        }

        if (payload.error) {
          this.toastAlert({
            icon: 'error',
            title: 'Error',
            text: payload.message,
          });
        } else {
          var stats = payload.data || {};
          this.toastAlert({
            icon: 'success',
            title: 'Renamed',
            text:
              'Students: ' +
              (stats.students_updated || 0) +
              ', employment info: ' +
              (stats.employment_informations_updated || 0),
            timer: 2500,
          });
          this.closeForm();
          this.loadList();
          this.loadStatistics();
        }
      } catch (err) {
        this.toastAlert({
          icon: 'error',
          title: 'Failed',
          text: 'Could not rename employer.',
        });
      } finally {
        this.saving = false;
      }
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ── Summary card ── */
.em-stat-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1.25rem 1rem;
  border-radius: 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.2s, transform 0.2s;
}
.em-stat-card--clickable {
  cursor: pointer;
  width: 100%;
}
.em-stat-card--clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px -10px rgba(15, 23, 42, 0.2);
}
.em-stat-card--clickable:disabled {
  cursor: default;
  transform: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.em-stat-card__icon {
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  font-size: 1.125rem;
}
.em-stat-card__body {
  min-width: 0;
}
.em-stat-card__value {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
  color: #1e293b;
}
.em-stat-card__split {
  font-size: 1rem;
  font-weight: 700;
  color: #94a3b8;
}
.em-stat-card__label {
  font-size: 0.8125rem;
  font-weight: 600;
  margin: 0.125rem 0 0;
  color: #475569;
}
.em-stat-card__hint {
  font-size: 0.6875rem;
  margin: 0.25rem 0 0;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.em-stat-card--total .em-stat-card__icon {
  background: #dbeafe;
  color: #2563eb;
}
.em-stat-card--students {
  border-left: 3px solid #0ea5e9;
}
.em-stat-card--students .em-stat-card__icon {
  background: #e0f2fe;
  color: #0284c7;
}
.em-stat-card--top {
  border-left: 3px solid #d97706;
}
.em-stat-card--top .em-stat-card__icon {
  background: #fef3c7;
  color: #d97706;
}
.em-stat-card--coverage {
  border-left: 3px solid #059669;
}
.em-stat-card--coverage .em-stat-card__icon {
  background: #d1fae5;
  color: #059669;
}

/* ── Table ── */
.em-select {
  padding: 0.5rem 0.625rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  outline: none;
  background: #fff;
  min-width: 12rem;
  transition: border-color 0.2s;
}
.em-select:focus {
  border-color: #3b82f6;
}
.em-thead-row {
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
}
.em-th {
  padding: 0.75rem 1.25rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}
.em-sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  font-size: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  cursor: pointer;
  transition: color 0.15s ease;
}
.em-sort-btn:hover,
.em-sort-btn--active {
  color: #1e3a8a;
}
.em-sort-btn__icon {
  font-size: 0.75rem;
  opacity: 0.55;
}
.em-sort-btn--active .em-sort-btn__icon {
  opacity: 1;
  color: #2563eb;
}
.em-row {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
}
.em-row:hover {
  background: #f8fafc;
}
.em-td {
  padding: 0.625rem 1.25rem;
  font-size: 0.8125rem;
  color: #374151;
  vertical-align: middle;
}

.em-count-link {
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  font: inherit;
  font-weight: 700;
  color: #1e3a8a;
  cursor: pointer;
  text-decoration: underline;
}
.em-count-link:hover {
  color: #2563eb;
}

.em-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
}
.em-badge--yes {
  background: #dcfce7;
  color: #166534;
}
.em-badge--no {
  background: #f1f5f9;
  color: #64748b;
}

.em-btn-add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.55rem;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.15s;
}
.em-btn-add:hover {
  background: #dbeafe;
  color: #1e3a8a;
}
.em-btn-add:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.em-export-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-shrink: 0;
  white-space: nowrap;
  border-radius: 0.75rem;
  border: 1px solid rgba(16, 185, 129, 0.25);
  background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #052e1f;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}
.em-export-btn:hover {
  border-color: rgba(16, 185, 129, 0.45);
  background: linear-gradient(to bottom, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.14));
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}
.em-export-btn:disabled {
  pointer-events: none;
  opacity: 0.55;
  cursor: not-allowed;
}
.em-export-btn__icon {
  font-size: 1rem;
  color: #059669;
}

.em-action-btn {
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  border: none;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
  font-size: 0.8125rem;
  transition: all 0.15s;
}
.em-action-btn:hover {
  background: #e0e7ff;
  color: #4338ca;
}

/* ── Modal controls ── */
.em-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #374151;
  background: #fff;
  outline: none;
}
.em-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}
.em-input--readonly {
  background: #f8fafc;
  color: #64748b;
  border-color: #e5e7eb;
}
.em-textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #374151;
  background: #fff;
  outline: none;
  resize: vertical;
  min-height: 5rem;
  line-height: 1.45;
  font-family: inherit;
}
.em-textarea:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}
.em-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: box-shadow 0.2s;
}
.em-btn-primary:hover {
  box-shadow: 0 4px 12px -2px rgba(37, 99, 235, 0.45);
}
.em-btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}
.em-btn-outline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6b7280;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  cursor: pointer;
}
.em-btn-outline:hover {
  background: #f3f4f6;
}
</style>
